import { useState, useCallback } from 'react';
import { ActionItem, TaskStatus } from '../types';

interface UseDragDropProps {
  actionItems: ActionItem[];
  setActionItems: React.Dispatch<React.SetStateAction<ActionItem[]>>;
  handleStatusChange: (actionId: string, newStatus: TaskStatus) => Promise<void>;
}

export function useDragDrop({ actionItems, setActionItems, handleStatusChange }: UseDragDropProps) {
  const [draggedItem, setDraggedItem] = useState<string | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent, itemId: string) => {
    setDraggedItem(itemId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', itemId);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedItem(null);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, newStatus: TaskStatus) => {
      e.preventDefault();
      if (draggedItem) {
        handleStatusChange(draggedItem, newStatus);
        setDraggedItem(null);
      }
    },
    [draggedItem, handleStatusChange]
  );

  const handleCardDragOver = useCallback(
    (e: React.DragEvent, targetItemId: string) => {
      e.preventDefault();
      e.stopPropagation();

      if (!draggedItem || draggedItem === targetItemId) return;

      const draggedIndex = actionItems.findIndex((item) => item.id === draggedItem);
      const targetIndex = actionItems.findIndex((item) => item.id === targetItemId);

      if (draggedIndex === -1 || targetIndex === -1) return;

      const newItems = [...actionItems];
      const [draggedCard] = newItems.splice(draggedIndex, 1);
      newItems.splice(targetIndex, 0, draggedCard);

      setActionItems(newItems);
    },
    [draggedItem, actionItems, setActionItems]
  );

  return {
    draggedItem,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDrop,
    handleCardDragOver,
  };
}
